/**
 * CTRL_04 Plugin - Explainability & Transparency
 * Enhanced UI for explainability method selection and confidence tracking
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Tag,
  Progress,
  Alert,
  Statistic,
  Divider,
  Space,
  Badge,
} from 'antd';
import {
  BulbOutlined,
  CheckCircleOutlined,
  LineChartOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors, spacing } from '../../../../theme/designTokens';

/**
 * Explainability Method Card
 */
function MethodCard({ method, enabled, onToggle, coverage = 0 }) {
  const methods = {
    SHAP: {
      icon: '🎯',
      name: 'SHAP',
      fullName: 'SHapley Additive exPlanations',
      complexity: 'high',
      useCase: 'Feature importance, global/local explanations',
    },
    LIME: {
      icon: '🔍',
      name: 'LIME',
      fullName: 'Local Interpretable Model-agnostic',
      complexity: 'medium',
      useCase: 'Local predictions, black-box models',
    },
    attention: {
      icon: '🧠',
      name: 'Attention',
      fullName: 'Attention Mechanisms',
      complexity: 'high',
      useCase: 'Neural networks, transformer models',
    },
    gradient: {
      icon: '📊',
      name: 'Gradient',
      fullName: 'Integrated Gradients',
      complexity: 'high',
      useCase: 'Deep learning models, attribution',
    },
    counterfactual: {
      icon: '🔄',
      name: 'Counterfactual',
      fullName: 'What-if Scenarios',
      complexity: 'medium',
      useCase: 'Actionable insights, decision support',
    },
  };

  const methodInfo = methods[method] || { icon: '📄', name: method, fullName: method, complexity: 'low', useCase: '' };

  const complexityColors = {
    high: 'red',
    medium: 'gold',
    low: 'green',
  };

  return (
    <Card
      size="small"
      hoverable
      onClick={onToggle}
      style={{
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: enabled ? colors.primary : '#E4E1E8',
        backgroundColor: enabled ? '#F4F1FA' : '#FFFFFF',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        height: '100%',
      }}
      bodyStyle={{ padding: spacing.md, minHeight: 120 }}
    >
      <Row align="middle" gutter={8} wrap={false}>
        <Col flex="none">
          <div style={{ fontSize: 32, lineHeight: 1 }}>{methodInfo.icon}</div>
        </Col>
        <Col flex="auto">
          <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            {methodInfo.name}
            {enabled && (
              <CheckCircleOutlined
                style={{ color: colors.status.pass, fontSize: 16 }}
              />
            )}
          </div>
          <div style={{
            fontSize: 11,
            color: 'rgba(23, 5, 43, 0.65)',
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {methodInfo.fullName}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
            <Tag color={complexityColors[methodInfo.complexity]} style={{ fontSize: 10, margin: 0, fontWeight: 600 }}>
              {methodInfo.complexity.toUpperCase()}
            </Tag>
            {coverage > 0 && (
              <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                {coverage}% coverage
              </Tag>
            )}
          </div>
          <div style={{ fontSize: 10, color: 'rgba(23, 5, 43, 0.55)' }}>
            {methodInfo.useCase}
          </div>
        </Col>
      </Row>
    </Card>
  );
}

/**
 * Explainability Stats Dashboard
 */
function ExplainabilityStats({ config }) {
  const methods = config?.required_explainability_methods || [];
  const confidenceThreshold = config?.minimum_confidence_threshold || 70;
  const qualityThreshold = config?.explanation_quality_threshold || 60;
  const requireConfidence = config?.require_confidence_scores || false;

  return (
    <Row gutter={[spacing.md, spacing.md]}>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Methods Enabled"
            value={methods.length}
            suffix="/ 5"
            prefix={<BulbOutlined style={{ color: colors.primary }} />}
            valueStyle={{ color: colors.primary }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Confidence Threshold"
            value={confidenceThreshold}
            suffix="%"
            prefix={<LineChartOutlined style={{ color: colors.severity.medium }} />}
            valueStyle={{ color: colors.severity.medium }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Quality Threshold"
            value={qualityThreshold}
            suffix="%"
            prefix={<SafetyOutlined style={{ color: colors.status.pass }} />}
            valueStyle={{ color: colors.status.pass }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Confidence Scores"
            value={requireConfidence ? 'Required' : 'Optional'}
            prefix={
              requireConfidence ? (
                <CheckCircleOutlined style={{ color: colors.status.pass }} />
              ) : (
                <SafetyOutlined style={{ color: colors.severity.medium }} />
              )
            }
            valueStyle={{
              color: requireConfidence ? colors.status.pass : colors.severity.medium,
              fontSize: 20,
            }}
          />
        </Card>
      </Col>
    </Row>
  );
}

/**
 * CTRL_04 Plugin Component
 */
function CTRL_04_Plugin({ projectId, controlCode }) {
  const [showDashboard, setShowDashboard] = useState(true);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    // In production, fetch actual config
    setConfig({
      enabled: false,
      required_explainability_methods: ['SHAP', 'LIME'],
      require_confidence_scores: true,
      minimum_confidence_threshold: 70,
      customer_facing_only: false,
      explanation_quality_threshold: 60,
    });
  }, [projectId, controlCode]);

  const handleToggleMethod = (method) => {
    setConfig((prev) => {
      const currentMethods = prev.required_explainability_methods || [];
      const isEnabled = currentMethods.includes(method);

      return {
        ...prev,
        required_explainability_methods: isEnabled
          ? currentMethods.filter((m) => m !== method)
          : [...currentMethods, method],
      };
    });
  };

  return (
    <div>
      {showDashboard && config && (
        <>
          <Alert
            message="Explainability & Transparency Dashboard"
            description="Visual interface for configuring explainability requirements. Select methods your models must support for transparent AI decision-making."
            type="info"
            showIcon
            closable
            onClose={() => setShowDashboard(false)}
            style={{ marginBottom: spacing.lg }}
          />

          <Card
            title={
              <Space>
                <BulbOutlined />
                Explainability Overview
              </Space>
            }
            style={{ marginBottom: spacing.lg }}
          >
            <ExplainabilityStats config={config} />
          </Card>

          <Divider orientation="left">Explainability Methods</Divider>

          <Alert
            message="Select Required Methods"
            description="Click cards to toggle methods. Models must support selected methods to pass compliance."
            type="success"
            showIcon
            style={{ marginBottom: spacing.md }}
          />

          <Row gutter={[spacing.md, spacing.md]} style={{ marginBottom: spacing.lg }}>
            {['SHAP', 'LIME', 'attention', 'gradient', 'counterfactual'].map(
              (method) => (
                <Col xs={24} sm={12} md={8} key={method}>
                  <MethodCard
                    method={method}
                    enabled={config.required_explainability_methods?.includes(method)}
                    onToggle={() => handleToggleMethod(method)}
                    coverage={Math.floor(Math.random() * 40) + 60}
                  />
                </Col>
              )
            )}
          </Row>

          <Divider />
        </>
      )}

      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_04_Plugin;
